/**
 * Device storage for the provider onboarding Business-step draft (ONB-6).
 *
 * The draft holds personal data the applicant typed (legal name, contact
 * email, company and website), so it follows the account-scoped device-storage
 * rules (ADR-WOA1):
 *   - scoped: one key per Supabase user id, never read for another user;
 *   - bounded: a versioned envelope with a 7-day lifetime, so an abandoned
 *     form does not sit on a shared computer indefinitely;
 *   - cleared: on sign-out, on an account switch, and as soon as the provider
 *     record exists (the API owns the details from then on).
 *
 * Drafts written by older builds have no timestamp, so their age is unknown;
 * they are discarded rather than trusted.
 */

export const PROVIDER_ONBOARDING_DRAFT_PREFIX = "tinkr.provider.draft.";
export const PROVIDER_ONBOARDING_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DRAFT_VERSION = 1;

type DraftEnvelope = { v: typeof DRAFT_VERSION; savedAt: number; draft: Record<string, unknown> };

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The stored draft fields for this user, or null when absent, expired or unreadable. */
export function readProviderOnboardingDraft(
  userId: string | null,
  now: number = Date.now()
): Record<string, unknown> | null {
  if (!userId) return null;
  const store = storage();
  if (!store) return null;
  const key = PROVIDER_ONBOARDING_DRAFT_PREFIX + userId;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftEnvelope> | null;
    const fresh =
      parsed !== null &&
      typeof parsed === "object" &&
      parsed.v === DRAFT_VERSION &&
      typeof parsed.savedAt === "number" &&
      parsed.savedAt <= now + 60_000 &&
      now - parsed.savedAt < PROVIDER_ONBOARDING_DRAFT_TTL_MS &&
      parsed.draft !== null &&
      typeof parsed.draft === "object";
    if (!fresh) {
      store.removeItem(key);
      return null;
    }
    return parsed.draft as Record<string, unknown>;
  } catch {
    try { store.removeItem(key); } catch { /* storage is optional */ }
    return null;
  }
}

export function writeProviderOnboardingDraft(
  userId: string | null,
  draft: Record<string, unknown>,
  now: number = Date.now()
): void {
  if (!userId) return;
  const envelope: DraftEnvelope = { v: DRAFT_VERSION, savedAt: now, draft };
  try {
    storage()?.setItem(PROVIDER_ONBOARDING_DRAFT_PREFIX + userId, JSON.stringify(envelope));
  } catch {
    // Quota or private browsing: the wizard keeps working from memory.
  }
}

export function clearProviderOnboardingDraft(userId: string | null): void {
  if (!userId) return;
  try {
    storage()?.removeItem(PROVIDER_ONBOARDING_DRAFT_PREFIX + userId);
  } catch {
    // Storage is optional.
  }
}

/**
 * Remove every stored onboarding draft except the one owned by `keepUserId`
 * (pass null to remove all of them). Expired drafts of the kept user are
 * removed too.
 */
export function clearProviderOnboardingDrafts(keepUserId: string | null = null): void {
  const store = storage();
  if (!store) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key?.startsWith(PROVIDER_ONBOARDING_DRAFT_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      if (keepUserId && key === PROVIDER_ONBOARDING_DRAFT_PREFIX + keepUserId) continue;
      store.removeItem(key);
    }
    if (keepUserId) readProviderOnboardingDraft(keepUserId);
  } catch {
    // Storage is optional.
  }
}
